"use client";

import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../types";
import type {
  SimpleTableContextValue,
  SimpleTableStableValue,
  SimpleTableVolatileValue,
  SimpleTableChromeValue,
} from "./simple-table-context.types";

export type {
  SimpleTableContextValue,
  SimpleTableStableValue,
  SimpleTableVolatileValue,
  SimpleTableChromeValue,
} from "./simple-table-context.types";

/**
 * Consolidated context for SimpleTable.
 *
 * THE VALUE IS SPLIT IN THREE (see `simple-table-context.types.ts` for the
 * partition and why): a context change bypasses `React.memo`, so one monolithic
 * value made `MemoizedTableRow` / `MemoizedTableCell` no-ops. Consumers that
 * only need one slice must read that slice's hook — `useSimpleTableContext()`
 * stays available as the composition and re-renders on ANY of the three.
 */

const SimpleTableStableContext = createContext<SimpleTableStableValue<BaseEntity> | undefined>(
  undefined
);
const SimpleTableVolatileContext = createContext<SimpleTableVolatileValue<BaseEntity> | undefined>(
  undefined
);
const SimpleTableChromeContext = createContext<SimpleTableChromeValue<BaseEntity> | undefined>(
  undefined
);

interface SimpleTableContextProviderProps<TData extends BaseEntity> {
  /**
   * Pre-split slices — what `SimpleTable` passes. Preferred: the three halves
   * are memoized independently upstream, which is the whole point of the split.
   */
  stable?: SimpleTableStableValue<TData>;
  volatile?: SimpleTableVolatileValue<TData>;
  chrome?: SimpleTableChromeValue<TData>;
  /**
   * Composed value. Kept for call sites that build the whole object (tests,
   * embedded harnesses); split here instead, which gives correctness but not
   * the re-render win since the composed object is one reference.
   */
  value?: SimpleTableContextValue<TData>;
  children: React.ReactNode;
}

function splitComposed<TData extends BaseEntity>(value: SimpleTableContextValue<TData>): {
  stable: SimpleTableStableValue<TData>;
  volatile: SimpleTableVolatileValue<TData>;
  chrome: SimpleTableChromeValue<TData>;
} {
  const {
    config,
    visibleFields,
    data,
    callbacks,
    refs,
    auth,
    hooks,
    onEdit,
    onView,
    onDelete,
    onInlineEdit,
    onOpenDetailModal,
    state,
    utils,
    ...chrome
  } = value;
  return {
    stable: {
      config,
      visibleFields,
      data,
      callbacks,
      refs,
      auth,
      hooks,
      onEdit,
      onView,
      onDelete,
      onInlineEdit,
      onOpenDetailModal,
    },
    volatile: { state, utils },
    chrome,
  };
}

/**
 * Provider component for SimpleTableContext.
 *
 * The context is stored at the `BaseEntity` boundary (it must serve every
 * entity type polymorphically); each consumer re-narrows via its slice hook.
 */
export function SimpleTableContextProvider<TData extends BaseEntity>({
  stable,
  volatile,
  chrome,
  value,
  children,
}: SimpleTableContextProviderProps<TData>) {
  const split = React.useMemo(() => (value ? splitComposed(value) : undefined), [value]);

  const stableValue = stable ?? split?.stable;
  const volatileValue = volatile ?? split?.volatile;
  const chromeValue = chrome ?? split?.chrome;

  if (!stableValue || !volatileValue || !chromeValue) {
    throw new Error(
      "SimpleTableContextProvider requires either `value` or all of `stable`/`volatile`/`chrome`"
    );
  }

  return (
    <SimpleTableStableContext.Provider
      value={stableValue as unknown as SimpleTableStableValue<BaseEntity>}
    >
      <SimpleTableVolatileContext.Provider
        value={volatileValue as unknown as SimpleTableVolatileValue<BaseEntity>}
      >
        <SimpleTableChromeContext.Provider
          value={chromeValue as unknown as SimpleTableChromeValue<BaseEntity>}
        >
          {children}
        </SimpleTableChromeContext.Provider>
      </SimpleTableVolatileContext.Provider>
    </SimpleTableStableContext.Provider>
  );
}

function missing(slice: string): never {
  throw new Error(`useSimpleTable${slice} must be used within SimpleTableContextProvider`);
}

/**
 * The STABLE slice: config, columns, data, callbacks, refs, auth, entity hooks
 * and the row action handlers. Read this (not the composition) from anything
 * that renders per row or per cell.
 */
export function useSimpleTableStable<
  TData extends BaseEntity = BaseEntity,
>(): SimpleTableStableValue<TData> {
  const context = useContext(SimpleTableStableContext);
  if (!context) missing("Stable");
  return context as unknown as SimpleTableStableValue<TData>;
}

/** The VOLATILE slice: interaction state + the column-style util it feeds. */
export function useSimpleTableVolatile<
  TData extends BaseEntity = BaseEntity,
>(): SimpleTableVolatileValue<TData> {
  const context = useContext(SimpleTableVolatileContext);
  if (!context) missing("Volatile");
  return context as unknown as SimpleTableVolatileValue<TData>;
}

/** The CHROME slice: header / body / pagination / modal-only values. */
export function useSimpleTableChrome<
  TData extends BaseEntity = BaseEntity,
>(): SimpleTableChromeValue<TData> {
  const context = useContext(SimpleTableChromeContext);
  if (!context) missing("Chrome");
  return context as unknown as SimpleTableChromeValue<TData>;
}

/**
 * The whole context, composed from the three slices.
 *
 * Every pre-split call site keeps working through this. It re-renders on a
 * change to ANY slice, so per-row / per-cell consumers should read the narrow
 * hooks above instead.
 */
export function useSimpleTableContext<
  TData extends BaseEntity = BaseEntity,
>(): SimpleTableContextValue<TData> {
  const stable = useSimpleTableStable<TData>();
  const volatile = useSimpleTableVolatile<TData>();
  const chrome = useSimpleTableChrome<TData>();
  return React.useMemo(
    () => ({ ...stable, ...volatile, ...chrome }),
    [stable, volatile, chrome]
  );
}
